import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import dayjs from 'lib/dayjs';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useCurrentConversationValue } from 'hooks/business/conversation';
import { TradeDetailLogisticsBlock, TradeDetailTextBlock } from './tradeDetailBlockTypes';

const formatTimestamp = (timestamp: string | number) =>
  dayjs(Number(timestamp)).format('YYYY-MM-DD HH:mm:ss');

const buildLatestLogisticsTimeBlock = (
  t: ReturnType<typeof useTranslate>['t'],
  latestTime: string | number,
): TradeDetailTextBlock => {
  return {
    id: 'latestLogisticsTime',
    type: 'text',
    label: t('trade.latestLogisticsTime'),
    value: formatTimestamp(Number(latestTime) * 1000),
  };
};

interface UseTradeLogisticsBlocksOptions {
  sendable?: boolean;
}

export interface UseTradeLogisticsBlocksResult {
  logisticsInfo?: TradeDetailLogisticsBlock;
  thirdLogisticsInfo?: TradeDetailLogisticsBlock;
  latestLogisticsTime?: TradeDetailTextBlock;
}

export const useTradeLogisticsBlocks = (
  trade: Conversation.Trade.TradeWithLogistics,
  options: UseTradeLogisticsBlocksOptions = {},
): UseTradeLogisticsBlocksResult => {
  const { sendable = true } = options;
  const { t } = useTranslate();
  const conversation = useCurrentConversationValue();
  const channel = conversation?.channel;

  return useMemo(() => {
    const { logisticsList, thirdLogisticsList, lazadaLogistics, mercadoLogistics } = trade;
    const blocks: UseTradeLogisticsBlocksResult = {};

    if (logisticsList?.length || thirdLogisticsList?.length) {
      const latestFirstTime = logisticsList?.[0]?.trackingInfo?.[0]?.updateTime || 0;
      const latestThirdTime = thirdLogisticsList?.[0]?.trackingInfo?.[0]?.updateTime || 0;
      const latestTime = Math.max(Number(latestFirstTime), Number(latestThirdTime));

      if (latestTime > 0) {
        blocks.latestLogisticsTime = buildLatestLogisticsTimeBlock(t, latestTime);
      }

      if (logisticsList?.length) {
        blocks.logisticsInfo = {
          id: 'logisticsInfo',
          type: 'logistics',
          title: t('trade.logisticsInfo'),
          trackingLabel: t(`leChatLogisticsStatus.${logisticsList[0].leChatStatus}`),
          payload: {
            kind: 'common',
            logisticsList,
            showSendLogistics:
              isTiKTokUpperCase(channel) && Boolean(conversation) && Boolean(sendable),
          },
        };
      }

      if (thirdLogisticsList?.length) {
        const { erpType, originStatus, leChatStatus } = thirdLogisticsList[0];

        const thirdStatusMap: Record<string, string> = {
          CREATED: t('待揽收'),
          IN_TRANSIT: t('运输中'),
          OUT_FOR_DELIVERY: t('投递中'),
          DELIVERED: t('aide.已签收'),
          NOT_FOUND: t('未查到'),
          UNDELIVERABLE: t('异常'),
        };

        blocks.thirdLogisticsInfo = {
          id: 'thirdLogisticsInfo',
          type: 'logistics',
          title: erpType ? `ERP ${t('trade.logisticsInfo')}` : t('trade.功夫快运'),
          trackingLabel:
            erpType && originStatus
              ? thirdStatusMap[originStatus] || '-'
              : t(`leChatLogisticsStatus.${leChatStatus}`),
          payload: {
            kind: 'commonThird',
            logisticsList: thirdLogisticsList,
          },
        };
      }

      return blocks;
    }

    if (lazadaLogistics?.packages?.length) {
      const [firstPackage] = lazadaLogistics.packages;
      const latestTime = firstPackage.latestTrackingTime;

      if (latestTime) {
        blocks.latestLogisticsTime = buildLatestLogisticsTimeBlock(t, latestTime);
      }

      blocks.logisticsInfo = {
        id: 'logisticsInfo',
        type: 'logistics',
        title: t('trade.logisticsInfo'),
        trackingLabel: t(`leChatLogisticsStatus.${firstPackage.leChatStatus}`),
        payload: {
          kind: 'lazada',
          logisticsList: lazadaLogistics.packages,
        },
      };

      return blocks;
    }

    if (mercadoLogistics) {
      blocks.logisticsInfo = {
        id: 'logisticsInfo',
        type: 'logistics',
        title: t('trade.logisticsInfo'),
        trackingLabel: t(`leChatLogisticsStatus.${mercadoLogistics.leChatStatus}`),
        payload: {
          kind: 'mercado',
          logistics: mercadoLogistics,
        },
      };
    }

    return blocks;
  }, [trade, channel, conversation, sendable, t]);
};
